import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/hooks/use-auth';
import { supabase } from '@/integrations/supabase/client';
import { lovable } from '@/integrations/lovable';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import {
  Lock,
  Mail,
  ArrowRight,
  UserPlus,
  LogIn,
  ArrowLeft,
  Sun,
  Moon,
} from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Helmet } from 'react-helmet-async';
import { z } from 'zod';
import { useTheme } from '@/hooks/use-theme';
import alphaWordmarkLight from '@/assets/alpha-newsletter/wordmark-ink.svg';
import alphaWordmarkDark from '@/assets/alpha-newsletter/wordmark-ivory.svg';
import alphaMarkLight from '@/assets/alpha-newsletter/mark-light.svg';
import alphaMarkDark from '@/assets/alpha-newsletter/mark-dark.svg';
import { LogoMark, Wordmark } from '@/components/brand/Logo';

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
});

type AuthMode = 'login' | 'signup' | 'forgot';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [promotionalEmails, setPromotionalEmails] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [mode, setMode] = useState<AuthMode>('login');
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [unconfirmedEmail, setUnconfirmedEmail] = useState<string | null>(null);
  const [linkSentTo, setLinkSentTo] = useState<string | null>(null);
  const [firstName, setFirstName] = useState('');

  const { signIn, resetPassword, user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { theme, toggleTheme } = useTheme();

  // One sign-in for the whole site; it leads with Data (his ruling, 30 Oct 2026). Only a reader sent here
  // from a Club page sees the Club's own heading and panel.
  const redirectTo = searchParams.get('redirect') || '/dashboard';
  const fromClub = redirectTo.startsWith('/ai-investing-club') || /[?&](subscribe|founding)=1/.test(redirectTo);

  // Read initial mode from URL query param
  useEffect(() => {
    // the address decides the form, so the menu's "Log in" leaves the sign-up form it opened
    const urlMode = searchParams.get('mode');
    setMode(urlMode === 'signup' || urlMode === 'forgot' ? urlMode : 'login');
  }, [searchParams]);

  useEffect(() => {
    if (!authLoading && user) {
      smartRoute(user.id, user.email || '');
    }
  }, [authLoading, user]);

  const smartRoute = async (userId: string, userEmail: string) => {
    const [mentorshipResult, adminResult] = await Promise.all([
      supabase
        .from('user_enrollments')
        .select('*, products:product_id (type)')
        .or(`user_id.eq.${userId},user_email.eq.${userEmail}`)
        .eq('payment_verified', true)
        .eq('access_granted', true),
      supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId)
        .eq('role', 'admin')
        .maybeSingle(),
    ]);

    const enrollments = mentorshipResult.data || [];
    const hasMentorship = enrollments.some((e: any) => {
      const product = e.products as { type: string } | null;
      return product?.type === 'mentorship';
    });
    const isAdminUser = !!adminResult.data;

    // an AI assistant's connect page (31 Oct 2026) always gets its reader back, admin or not
    if (redirectTo.startsWith('/data/connect?')) {
      navigate(redirectTo);
    } else if (hasMentorship || isAdminUser) {
      navigate('/dashboard');
    } else {
      navigate(redirectTo);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    const validation = loginSchema.safeParse({ email, password });
    if (!validation.success) {
      toast({ title: 'Validation Error', description: validation.error.errors[0].message, variant: 'destructive' });
      return;
    }

    setIsLoading(true);
    setUnconfirmedEmail(null);
    const { error } = await signIn(email, password);

    if (error) {
      setIsLoading(false);
      let message = 'An error occurred during login';
      if (error.message.includes('Invalid login credentials')) {
        message = 'Invalid email or password.';
      } else if (error.message.includes('Email not confirmed')) {
        message = 'Please check your email (including spam/promotions folders) to confirm your account.';
        setUnconfirmedEmail(email);
      }
      toast({ title: 'Login Failed', description: message, variant: 'destructive' });
      return;
    }

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      setIsLoading(false);
      toast({ title: 'Login Failed', description: 'Unable to verify user session.', variant: 'destructive' });
      return;
    }

    await smartRoute(user.id, user.email || '');
    setIsLoading(false);
    toast({ title: 'Welcome back!', description: 'You have successfully logged in.' });
  };

  // Sign-up by email link (his ruling, 2 Nov 2026): the reader gives only an address and gets a link; the link
  // opens the account (so nobody can make one with someone else's address) and leads to setting a password.
  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!z.string().email().safeParse(email.trim()).success) {
      toast({ title: 'Invalid Email', description: 'Please enter a valid email address.', variant: 'destructive' });
      return;
    }
    setIsLoading(true);
    const { error } = await supabase.functions.invoke('auth-signup-resend', {
      body: {
        email,
        name: firstName.trim(),
        signup: true,
        promotionalEmails,
        redirectTo: `${window.location.origin}/login?redirect=${encodeURIComponent(redirectTo)}`,
        setPasswordTo: `${window.location.origin}/reset-password?new=1&redirect=${encodeURIComponent(redirectTo)}`,
      },
    });
    setIsLoading(false);
    if (error) {
      toast({ title: 'Sign up failed', description: error.message || 'Unable to send the email.', variant: 'destructive' });
      return;
    }
    setLinkSentTo(email.trim());
  };

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    try {
      const result = await lovable.auth.signInWithOAuth('google', {
        redirect_uri: `${window.location.origin}/login?redirect=${encodeURIComponent(redirectTo)}`,
      });
      if (result.error) {
        toast({ title: 'Google Sign In Failed', description: String(result.error), variant: 'destructive' });
      }
    } catch (err) {
      toast({ title: 'Google Sign In Failed', description: 'An unexpected error occurred.', variant: 'destructive' });
    }
    setIsLoading(false);
  };

  const handleResendVerification = async () => {
    if (!unconfirmedEmail) return;
    setIsLoading(true);
    const { error } = await supabase.functions.invoke('auth-signup-resend', {
      body: {
        email: unconfirmedEmail,
        redirectTo: `${window.location.origin}/login?redirect=${encodeURIComponent(redirectTo)}`,
      },
    });
    setIsLoading(false);
    
    if (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } else {
      toast({ title: 'Verification Sent', description: 'A new verification link has been sent. Please check your inbox and spam folder.' });
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !z.string().email().safeParse(email).success) {
      toast({ title: 'Invalid Email', description: 'Please enter a valid email address.', variant: 'destructive' });
      return;
    }
    setIsLoading(true);
    const { error } = await resetPassword(email);
    setIsLoading(false);
    if (error) {
      toast({ title: 'Error', description: 'Failed to send reset email.', variant: 'destructive' });
      return;
    }
    setResetEmailSent(true);
    toast({ title: 'Reset Email Sent', description: 'Check your inbox for password reset instructions.' });
  };

  const modeTitle =
    mode === 'signup'
      ? 'Make a free account'
      : mode === 'forgot'
      ? 'Reset access'
      : fromClub
      ? 'Sign in to the Club'
      : 'Sign in to Shikshan Nivesh';
  const modeSubtitle =
    mode === 'signup'
      ? 'The last 5 years on every company page, 10 downloads a month and a free API key.'
      : mode === 'forgot'
      ? 'Enter your email to receive reset instructions.'
      : fromClub
      ? 'For members of the Alpha with AI Club.'
      : 'Your data plan, API keys and downloads.';

  // Signed in (a password login, or back from Google): smartRoute is picking the page, so show that
  // instead of the form, which otherwise flashes for the second the two lookups take.
  if (user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-[15px] text-muted-foreground">Signing you in…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col lg:flex-row">
      <Helmet>
        <title>{fromClub && mode === 'login' ? `${modeTitle} | Alpha with AI Club` : mode === 'login' ? 'Sign in | Shikshan Nivesh' : `${modeTitle} | Shikshan Nivesh`}</title>
        <meta
          name="description"
          content="Sign in to Shikshan Nivesh for your data plan, API keys and downloads of US company data."
        />
        <link rel="canonical" href="https://shikshannivesh.com/login" />
      </Helmet>

      {/* ─── Left Club Pitch Panel ─── */}
      <div className="relative lg:w-[45%] bg-card flex flex-col justify-between p-8 lg:p-12 overflow-hidden">

        {/* Top: Back link */}
        <div className="relative z-10 flex items-center justify-between">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-accent transition-colors group"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
            Back to Website
          </Link>
        </div>

        {fromClub ? (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="relative z-10 flex-1 flex flex-col justify-center py-10 lg:py-0"
        >
          {/* Wordmark */}
          <div className="flex items-center gap-2 mb-6">
            <img src={alphaMarkLight} alt="" aria-hidden className="block dark:hidden h-10 w-auto" />
            <img src={alphaMarkDark} alt="" aria-hidden className="hidden dark:block h-10 w-auto" />
            <img src={alphaWordmarkLight} alt="alpha with ai" className="block dark:hidden h-8 w-auto" />
            <img src={alphaWordmarkDark} alt="alpha with ai" className="hidden dark:block h-8 w-auto" />
            <span className="font-serif italic text-2xl text-accent ml-1">club.</span>
          </div>

          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent mb-3">
            Members workspace
          </p>

          <h2 className="text-3xl lg:text-4xl font-bold leading-tight text-foreground mb-4">
            The private workspace for{' '}
            <span className="text-accent">analysts using AI.</span>
          </h2>

          <p className="text-base text-muted-foreground mb-8 max-w-md leading-relaxed">
            AI playbooks, member threads, and the global analyst map - all in
            one private room for working analysts.
          </p>

          <ul className="space-y-2.5 mb-10 text-sm text-muted-foreground">
            <li className="flex items-baseline gap-3">
              <span className="h-1 w-1 rounded-full bg-accent shrink-0 translate-y-[-2px]" />
              <span><span className="text-foreground font-medium">AI Playbooks</span> - step-by-step AI workflows from real client work.</span>
            </li>
            <li className="flex items-baseline gap-3">
              <span className="h-1 w-1 rounded-full bg-accent shrink-0 translate-y-[-2px]" />
              <span><span className="text-foreground font-medium">Member Threads</span> - private discussions with other analysts.</span>
            </li>
            <li className="flex items-baseline gap-3">
              <span className="h-1 w-1 rounded-full bg-accent shrink-0 translate-y-[-2px]" />
              <span><span className="text-foreground font-medium">Global Analyst Map</span> - see where Club members work from.</span>
            </li>
          </ul>
        </motion.div>
        ) : (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="relative z-10 flex-1 flex flex-col justify-center py-10 lg:py-0"
        >
          <div className="flex items-center gap-3 mb-6">
            <LogoMark size={40} />
            <Wordmark size={20} />
          </div>

          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent mb-3">
            SN Data
          </p>

          <h2 className="text-3xl lg:text-4xl font-bold leading-tight text-foreground mb-8">
            One account for the site, your downloads and the API.
          </h2>

          <ul className="space-y-2.5 mb-10 text-sm text-muted-foreground">
            <li className="flex items-baseline gap-3">
              <span className="h-1 w-1 rounded-full bg-accent shrink-0 translate-y-[-2px]" />
              <span><span className="text-foreground font-medium">Company pages</span>: results, statements, insider trades and fund holdings for every covered company.</span>
            </li>
            <li className="flex items-baseline gap-3">
              <span className="h-1 w-1 rounded-full bg-accent shrink-0 translate-y-[-2px]" />
              <span><span className="text-foreground font-medium">Downloads</span>: any table as a CSV or JSON file.</span>
            </li>
            <li className="flex items-baseline gap-3">
              <span className="h-1 w-1 rounded-full bg-accent shrink-0 translate-y-[-2px]" />
              <span><span className="text-foreground font-medium">The API</span>: the same records for your own code, a spreadsheet or an AI assistant.</span>
            </li>
          </ul>
        </motion.div>
        )}

        <div className="relative z-10 mt-6 lg:mt-0 space-y-1">
          <p className="text-xs text-muted-foreground">
            © {new Date().getFullYear()} Shikshan Nivesh. All rights reserved.
          </p>
        </div>
      </div>

      {/* ─── Right Auth Panel ─── */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        {/* Theme toggle */}
        <button
          type="button"
          onClick={toggleTheme}
          aria-label="Toggle theme"
          className="fixed top-4 right-4 z-20 inline-flex items-center justify-center h-9 w-9 rounded-full border border-border/50 bg-card/60 text-muted-foreground hover:text-accent hover:border-accent/40 transition-colors backdrop-blur-sm"
        >
          {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
        </button>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25, delay: 0.2 }}
          className="w-full max-w-md"
        >
          {/* Header */}
          <div className="mb-8">
            <p className="text-sm font-serif text-accent mb-1">
              {mode === 'signup' ? 'Free account' : mode === 'forgot' ? 'No worries' : 'Welcome back'}
            </p>
            <h1 className="text-2xl font-bold text-foreground mb-2">{modeTitle}</h1>
            <p className="text-muted-foreground text-sm">{modeSubtitle}</p>
          </div>

          {mode === 'signup' && linkSentTo ? (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-4">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-accent/20 flex items-center justify-center">
                <Mail className="w-8 h-8 text-accent" />
              </div>
              <p className="text-foreground mb-2">Check your inbox</p>
              <p className="text-sm text-muted-foreground">
                We sent a link to <span className="text-accent">{linkSentTo}</span>. Click it to open your account. If it is not there in a minute, look in Spam or Promotions.
              </p>
              <button type="button" onClick={handleSignup} disabled={isLoading}
                className="mt-5 text-sm text-accent hover:text-accent/80 transition-colors font-medium">
                Send the link again
              </button>
              <button type="button" onClick={() => setLinkSentTo(null)}
                className="block mx-auto mt-2 text-sm text-muted-foreground hover:text-accent transition-colors">
                Use a different email
              </button>
            </motion.div>
          ) : mode === 'forgot' ? (
            <form onSubmit={handleForgotPassword} className="space-y-6">
              {resetEmailSent ? (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-4">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-accent/20 flex items-center justify-center">
                    <Mail className="w-8 h-8 text-accent" />
                  </div>
                  <p className="text-foreground mb-2">Check your inbox!</p>
                  <p className="text-sm text-muted-foreground">
                    We've sent password reset instructions to <span className="text-accent">{email}</span>
                  </p>
                </motion.div>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="reset-email" className="text-foreground flex items-center gap-2">
                      <Mail className="w-4 h-4 text-accent" /> Email
                    </Label>
                    <Input
                      id="reset-email" type="email" placeholder="you@example.com"
                      value={email} onChange={(e) => setEmail(e.target.value)}
                      className="bg-background/50 border-border/50" required
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading ? 'Sending...' : 'Send Reset Instructions'}
                  </Button>
                </>
              )}
              <button type="button" onClick={() => { setMode('login'); setResetEmailSent(false); }}
                className="w-full text-center text-sm text-muted-foreground hover:text-accent transition-colors">
                ← Back to login
              </button>
            </form>
          ) : (
            <>
              {/* Google OAuth */}
              <Button
                type="button"
                variant="outline"
                className="w-full mb-4 gap-2"
                onClick={handleGoogleSignIn}
                disabled={isLoading}
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                </svg>
                Continue with Google
              </Button>

              <div className="relative mb-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border/50" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">or</span>
                </div>
              </div>

              <form onSubmit={mode === 'login' ? handleLogin : handleSignup} className="space-y-4">
                {mode === 'signup' && (
                  <div className="space-y-2">
                    <Label htmlFor="firstName" className="text-foreground flex items-center gap-2">
                      <UserPlus className="w-4 h-4 text-accent" /> First name
                    </Label>
                    <Input
                      id="firstName" type="text" placeholder="Your first name" autoComplete="given-name" maxLength={30}
                      value={firstName} onChange={(e) => setFirstName(e.target.value)}
                      className="bg-background/50 border-border/50" required
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-foreground flex items-center gap-2">
                    <Mail className="w-4 h-4 text-accent" /> Email
                  </Label>
                  <Input
                    id="email" type="email" placeholder="you@example.com"
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    className="bg-background/50 border-border/50" required
                  />
                </div>

                {mode === 'login' && (
                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-foreground flex items-center gap-2">
                      <Lock className="w-4 h-4 text-accent" /> Password
                    </Label>
                    <Input
                      id="password" type="password" placeholder="••••••••"
                      value={password} onChange={(e) => setPassword(e.target.value)}
                      className="bg-background/50 border-border/50" required
                    />
                  </div>
                )}

                {mode === 'signup' && (
                  <p className="text-xs text-muted-foreground">We will email you a link. Click it to open your account, then set a password.</p>
                )}

                {mode === 'signup' && (
                  <div className="space-y-3 rounded-lg border border-border/50 bg-background/30 p-4">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Email Preferences</p>
                    
                    <div className="flex items-start gap-3">
                      <Checkbox id="resource-updates" checked disabled className="mt-0.5 opacity-70" />
                      <div className="space-y-0.5">
                        <label htmlFor="resource-updates" className="text-sm text-foreground flex items-center gap-1.5">
                          Weekly Resource Updates
                          <span className="text-[10px] font-medium text-accent bg-accent/10 px-1.5 py-0.5 rounded">Required</span>
                        </label>
                        <p className="text-xs text-muted-foreground">We'll notify you when new research resources are published.</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="promotional-emails"
                        checked={promotionalEmails}
                        onCheckedChange={(checked) => setPromotionalEmails(checked === true)}
                        className="mt-0.5"
                      />
                      <div className="space-y-0.5">
                        <label htmlFor="promotional-emails" className="text-sm text-foreground cursor-pointer">
                          Product Updates & Offers
                        </label>
                        <p className="text-xs text-muted-foreground">Occasional product updates and offers. You can unsubscribe anytime.</p>
                      </div>
                    </div>
                  </div>
                )}

                <Button type="submit" className="w-full group" disabled={isLoading}>
                  {isLoading ? (
                    <span className="flex items-center gap-2">
                      <span className="animate-pulse">{mode === 'signup' ? 'Sending the link' : 'Signing in'}...</span>
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      {mode === 'signup' ? <><UserPlus className="w-4 h-4" /> Email me a link</> : <><LogIn className="w-4 h-4" /> Sign In</>}
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </span>
                  )}
                </Button>

                {unconfirmedEmail && mode === 'login' && (
                  <button type="button" onClick={handleResendVerification}
                    className="w-full text-center text-sm text-accent hover:text-accent/80 transition-colors font-medium">
                    Didn't receive the email? Resend verification link
                  </button>
                )}

                {mode === 'login' && (
                  <button type="button" onClick={() => setMode('forgot')}
                    className="w-full text-center text-sm text-muted-foreground hover:text-accent transition-colors">
                    Forgot password?
                  </button>
                )}

                <p className="text-center text-sm text-muted-foreground">
                  {mode === 'login' ? (
                    <>Don't have an account?{' '}
                      <button type="button" onClick={() => setMode('signup')} className="text-accent hover:underline">
                        Sign up free →
                      </button>
                    </>
                  ) : (
                    <>Already have an account?{' '}
                      <button type="button" onClick={() => setMode('login')} className="text-accent hover:underline">
                        Sign in →
                      </button>
                    </>
                  )}
                </p>
              </form>
            </>
          )}
          <p className="text-center text-xs text-muted-foreground/60 mt-6">
            {fromClub ? 'Alpha with AI Club · A members workspace by Shikshan Nivesh' : 'Shikshan Nivesh'}
          </p>
        </motion.div>
      </div>
    </div>
  );
}
